import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { aiTranslate, bulkDeprecateKeys, markReviewed, retranslate } from './actions'
import { api, query } from './client'
import { ME_KEY, queryClient } from './queryClient'
import { cellKey, useUi } from '../store/ui'
import type {
  ActivityPage,
  AdminUser,
  AiProvider,
  AiProviderName,
  ApiKeyCreated,
  ApiKeyRecord,
  ApiKeyScope,
  BulkCancelResult,
  EraseUserResult,
  Grid,
  History,
  Job,
  JobCancelFilter,
  JobList,
  KeyBulkDeprecateResult,
  KeyRecord,
  LocaleAssignResult,
  LocaleInfo,
  LocalePatchResult,
  LocaleUnassignResult,
  Me,
  Platform,
  PlatformRecord,
  Project,
  ProviderTestResult,
  PutTranslationResult,
  Role,
  TagBulkAction,
  TagBulkResult,
  TagCount,
  TagDeleteResult,
  TagRenameResult,
  TranslateChoice,
  UntranslatedCountResult,
} from './types'

/** Prefix shared by every grid variant of a project (source-locale grid and
 *  the include_deprecated toggle both hang off this same project) — every
 *  write invalidates via THIS key so it clears both `include_deprecated`
 *  variants at once, not just the one the mutating view happened to be on.
 *  useGrid appends `includeDeprecated` to build its own, longer, query key. */
export const gridKeyFor = (platform: string, projectId: string | undefined) =>
  ['grid', platform, projectId ?? 'default'] as const

// --------------------------------------------------------------------------
// Session
// --------------------------------------------------------------------------

export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    staleTime: 60_000,
    queryFn: () =>
      api<Me>('/api/auth/me', { allowUnauthorized: true }).catch((err) => {
        if (err instanceof Error && 'status' in err && (err as { status: number }).status === 401) {
          return null
        }
        throw err
      }),
  })
}

export function useLogin() {
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api<Me>('/api/auth/login', { method: 'POST', body, allowUnauthorized: true }),
    onSuccess: (me) => {
      queryClient.setQueryData(ME_KEY, me)
      queryClient.invalidateQueries()
    },
  })
}

export function useLogout() {
  return useMutation({
    mutationFn: () => api<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      queryClient.setQueryData(ME_KEY, null)
      queryClient.removeQueries({ predicate: (q) => q.queryKey !== ME_KEY })
    },
  })
}

/** Self-service rename: any signed-in user (admin or editor) may set or
 * change their own display name — no admin role required. */
export function usePatchMe() {
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (name: string) => api<Me>('/api/auth/me', { method: 'PATCH', body: { name } }),
    onSuccess: (me) => {
      queryClient.setQueryData(ME_KEY, me)
      pushToast('success', 'Name updated.')
    },
    onError: (error) => pushToast('error', `Could not update name: ${(error as Error).message}`),
  })
}

// --------------------------------------------------------------------------
// Reference data
// --------------------------------------------------------------------------

export function useLocales() {
  return useQuery({
    queryKey: ['locales'],
    staleTime: 5 * 60_000,
    queryFn: () => api<LocaleInfo[]>('/api/locales'),
  })
}

export function usePlatforms() {
  return useQuery({ queryKey: ['platforms'], queryFn: () => api<Platform[]>('/api/platforms') })
}

export function useProjects(platform: string | undefined) {
  return useQuery({
    queryKey: ['projects', platform],
    enabled: Boolean(platform),
    queryFn: () => api<Project[]>(`/api/platforms/${platform}/projects`),
  })
}

export function useJobs(status?: string, limit = 50) {
  return useQuery({
    queryKey: ['jobs', status ?? 'all', limit],
    refetchInterval: 15_000,
    queryFn: () => api<JobList>(`/api/jobs${query({ status, limit })}`),
  })
}

export function useRetryJob() {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (jobId: string) => api<Job>(`/api/jobs/${jobId}/retry`, { method: 'POST' }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['jobs'] })
      pushToast('success', 'Job re-queued.')
    },
    onError: (error) => pushToast('error', `Retry failed: ${(error as Error).message}`),
  })
}

/** The brake for a single wrong-target job: cancels a pending job cleanly, or
 * a running one (its provider result is discarded on write-back — see
 * ai/worker.py). Refused with a shaped error for a done/failed job. */
export function useCancelJob() {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (jobId: string) => api<Job>(`/api/jobs/${jobId}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['jobs'] })
      pushToast('success', 'Job cancelled.')
    },
    onError: (error) => pushToast('error', `Cancel failed: ${(error as Error).message}`),
  })
}

/** The brake for a bulk-translate mistake: cancels every pending/running job
 * matching the filter (project and/or locale, or `allPending` for every
 * active job) in one call, and reports exactly how many were cancelled. */
export function useBulkCancelJobs() {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (vars: JobCancelFilter) =>
      api<BulkCancelResult>('/api/jobs/cancel', {
        method: 'POST',
        body: {
          project_id: vars.projectId,
          locale: vars.locale,
          all_pending: vars.allPending ?? false,
        },
      }),
    onSuccess: (result) => {
      client.invalidateQueries({ queryKey: ['jobs'] })
      pushToast('success', `${result.cancelled} job${result.cancelled === 1 ? '' : 's'} cancelled.`)
    },
    onError: (error) => pushToast('error', `Bulk cancel failed: ${(error as Error).message}`),
  })
}

export function useGrid(
  platform: string | undefined,
  projectId: string | undefined,
  includeDeprecated = false,
) {
  return useQuery({
    // includeDeprecated is part of the key: toggling "show deprecated" must
    // fetch its own cache entry, not reuse (or silently overwrite) the
    // default view's — otherwise the toggle would serve stale rows from
    // whichever variant loaded first.
    queryKey: [...gridKeyFor(platform ?? '', projectId), includeDeprecated],
    // The project id comes from a separate query, so gating on the platform
    // alone would fire one unscoped request plus a second scoped one — two
    // full keys x locales payloads per editor load.
    enabled: Boolean(platform && projectId),
    staleTime: 30_000,
    queryFn: () =>
      api<Grid>(
        `/api/platforms/${platform}/grid${query({ project_id: projectId, include_deprecated: includeDeprecated })}`,
      ),
  })
}

export function useHistory(translationId: string | null) {
  return useQuery({
    queryKey: ['history', translationId],
    enabled: Boolean(translationId),
    queryFn: () => api<History>(`/api/translations/${translationId}/history`),
  })
}

// --------------------------------------------------------------------------
// Translation writes (optimistic)
// --------------------------------------------------------------------------

export interface PutVars {
  keyId: string
  locale: string
  pluralForm: string
  value: string
}

/** Immutably replace one cell in a cached grid. */
function patchGrid(
  grid: Grid,
  vars: PutVars,
  patch: (cell: Grid['keys'][number]['cells'][string][number]) => Grid['keys'][number]['cells'][string][number],
): Grid {
  return {
    ...grid,
    keys: grid.keys.map((key) => {
      if (key.id !== vars.keyId) return key
      const cells = key.cells[vars.locale] ?? []
      return {
        ...key,
        cells: {
          ...key.cells,
          [vars.locale]: cells.map((cell) =>
            cell.plural_form === vars.pluralForm ? patch(cell) : cell,
          ),
        },
      }
    }),
  }
}

export function usePutTranslation(
  platform: string,
  project: Project | undefined,
  includeDeprecated = false,
) {
  const client = useQueryClient()
  const setLint = useUi((state) => state.setLint)
  const pushToast = useUi((state) => state.pushToast)
  // Must match the exact key useGrid queried under (gridKeyFor + the toggle
  // state), not just the prefix: getQueryData/setQueryData need an exact
  // key, unlike invalidateQueries elsewhere in this file which matches by
  // prefix on purpose.
  const gk = [...gridKeyFor(platform, project?.id), includeDeprecated]

  return useMutation({
    mutationFn: (vars: PutVars) =>
      api<PutTranslationResult>('/api/translations', {
        method: 'PUT',
        body: {
          project_id: project?.id,
          key_id: vars.keyId,
          locale: vars.locale,
          plural_form: vars.pluralForm,
          value: vars.value,
        },
      }),
    onMutate: async (vars) => {
      await client.cancelQueries({ queryKey: gk })
      const previous = client.getQueryData<Grid>(gk)
      if (previous) {
        client.setQueryData<Grid>(
          gk,
          patchGrid(previous, vars, (cell) => ({
            ...cell,
            value: vars.value,
            status: 'manual',
            needs_review: false,
            stale: false,
            origin: 'own',
          })),
        )
      }
      return { previous }
    },
    onError: (error, vars, context) => {
      if (context?.previous) client.setQueryData(gk, context.previous)
      pushToast('error', `Save failed (${vars.locale}): ${(error as Error).message}`)
    },
    onSuccess: (result, vars) => {
      const ck = cellKey(vars.keyId, vars.locale, vars.pluralForm)
      setLint(ck, result.lint ?? [])
      const current = client.getQueryData<Grid>(gk)
      if (current) {
        client.setQueryData<Grid>(
          gk,
          patchGrid(current, vars, (cell) => ({
            ...cell,
            translation_id: result.translation.id,
            status: result.translation.status,
            needs_review: result.translation.needs_review,
          })),
        )
      }
      client.invalidateQueries({ queryKey: ['platforms'] })
      client.invalidateQueries({ queryKey: ['activity'] })
    },
  })
}

export function useRevert(platform: string, projectId: string | undefined) {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (revisionId: string) =>
      api<{ changed: boolean }>(`/api/revisions/${revisionId}/revert`, { method: 'POST' }),
    onSuccess: () => {
      pushToast('success', 'Reverted to the selected revision.')
      client.invalidateQueries({ queryKey: gridKeyFor(platform, projectId) })
      client.invalidateQueries({ queryKey: ['history'] })
      client.invalidateQueries({ queryKey: ['activity'] })
    },
    onError: (error) => pushToast('error', `Revert failed: ${(error as Error).message}`),
  })
}

// --------------------------------------------------------------------------
// Keys & projects
// --------------------------------------------------------------------------

export interface KeyCreateVars {
  name: string
  context: string
  is_plural: boolean
  tags: string[]
  source_value: string | Record<string, string>
}

export function useCreateKey(platform: string, projectId: string | undefined) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: KeyCreateVars) =>
      api<KeyRecord>(`/api/platforms/${platform}/keys`, { method: 'POST', body }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: gridKeyFor(platform, projectId) })
      client.invalidateQueries({ queryKey: ['platforms'] })
      client.invalidateQueries({ queryKey: ['jobs', 'all'] })
    },
  })
}

export interface KeyPatchVars {
  keyId: string
  name?: string
  context?: string
  tags?: string[]
  deprecated?: boolean
  is_plural?: boolean
}

export function usePatchKey(platform: string, projectId: string | undefined) {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: ({ keyId, ...body }: KeyPatchVars) =>
      api<KeyRecord>(`/api/keys/${keyId}`, { method: 'PATCH', body }),
    onSuccess: (_key, vars) => {
      client.invalidateQueries({ queryKey: gridKeyFor(platform, projectId) })
      if (vars.name !== undefined) client.invalidateQueries({ queryKey: ['platforms'] })
      if (vars.tags !== undefined) client.invalidateQueries({ queryKey: ['tags', platform] })
    },
    onError: (error) => pushToast('error', `Could not update key: ${(error as Error).message}`),
  })
}

// --------------------------------------------------------------------------
// Tags: authoritative list w/ counts, rename/merge, delete, bulk add/remove
// --------------------------------------------------------------------------

export function useTags(platform: string | undefined) {
  return useQuery({
    queryKey: ['tags', platform],
    enabled: Boolean(platform),
    queryFn: () => api<TagCount[]>(`/api/platforms/${platform}/tags`),
  })
}

export function useRenameTag(platform: string, projectId: string | undefined) {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (vars: { tag: string; newName: string }) =>
      api<TagRenameResult>(`/api/platforms/${platform}/tags/${encodeURIComponent(vars.tag)}`, {
        method: 'PATCH',
        body: { new_name: vars.newName },
      }),
    onSuccess: (result) => {
      client.invalidateQueries({ queryKey: ['tags', platform] })
      client.invalidateQueries({ queryKey: gridKeyFor(platform, projectId) })
      pushToast('success', `"${result.tag}" renamed to "${result.new_name}" on ${result.affected} key(s).`)
    },
    onError: (error) => pushToast('error', `Could not rename tag: ${(error as Error).message}`),
  })
}

export function useDeleteTag(platform: string, projectId: string | undefined) {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (tag: string) =>
      api<TagDeleteResult>(`/api/platforms/${platform}/tags/${encodeURIComponent(tag)}`, {
        method: 'DELETE',
      }),
    onSuccess: (result) => {
      client.invalidateQueries({ queryKey: ['tags', platform] })
      client.invalidateQueries({ queryKey: gridKeyFor(platform, projectId) })
      pushToast('success', `"${result.tag}" removed from ${result.affected} key(s).`)
    },
    onError: (error) => pushToast('error', `Could not delete tag: ${(error as Error).message}`),
  })
}

export function useBulkTag(platform: string, projectId: string | undefined) {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (vars: { tag: string; keyIds: string[]; action: TagBulkAction }) =>
      api<TagBulkResult>(
        `/api/platforms/${platform}/tags/${encodeURIComponent(vars.tag)}/bulk`,
        { method: 'POST', body: { key_ids: vars.keyIds, action: vars.action } },
      ),
    onSuccess: (result) => {
      client.invalidateQueries({ queryKey: ['tags', platform] })
      client.invalidateQueries({ queryKey: gridKeyFor(platform, projectId) })
      pushToast(
        'success',
        result.action === 'add'
          ? `Tag "${result.tag}" added to ${result.affected} key(s).`
          : `Tag "${result.tag}" removed from ${result.affected} key(s).`,
      )
    },
    onError: (error) => pushToast('error', `Could not update tag: ${(error as Error).message}`),
  })
}

export function useBulkDeprecateKeys(platform: string, projectId: string | undefined) {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (vars: { keyIds: string[]; deprecated: boolean }) =>
      bulkDeprecateKeys(platform, { key_ids: vars.keyIds, deprecated: vars.deprecated }),
    onSuccess: (result: KeyBulkDeprecateResult) => {
      // Both include_deprecated variants of this project's grid are stale:
      // the default view may have just lost rows, the "show deprecated" view
      // may have just gained (or lost) some.
      client.invalidateQueries({ queryKey: gridKeyFor(platform, projectId) })
      client.invalidateQueries({ queryKey: ['tags', platform] })
      pushToast(
        'success',
        result.deprecated
          ? `${result.affected} key(s) deprecated — removed from every export.`
          : `${result.affected} key(s) restored.`,
      )
    },
    onError: (error) => pushToast('error', `Bulk deprecate/restore failed: ${(error as Error).message}`),
  })
}

export function usePatchProject(platform: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      projectId,
      ...body
    }: { projectId: string; name?: string; description?: string }) =>
      api<Project>(`/api/projects/${projectId}`, { method: 'PATCH', body }),
    onSuccess: (project) => {
      client.invalidateQueries({ queryKey: ['projects', platform] })
      // setQueriesData (plural), not setQueryData: the cache holds one grid
      // entry per include_deprecated toggle value now, and both need the
      // refreshed project embedded, not just whichever one happened to be
      // fetched first under the bare prefix.
      client.setQueriesData<Grid>({ queryKey: gridKeyFor(platform, project.id) }, (grid) =>
        grid ? { ...grid, project } : grid,
      )
    },
  })
}

export function useCreateProject(platform: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { slug: string; name: string; description: string }) =>
      api<Project>(`/api/platforms/${platform}/projects`, { method: 'POST', body }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['projects', platform] }),
  })
}

/** Soft delete only — the default project can never be targeted (the route
 * 409s, and the UI never offers the button for it either). Translations and
 * revisions are kept; the project just stops resolving until restored. */
export function useDeleteProject(platform: string) {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (projectId: string) => api<Project>(`/api/projects/${projectId}`, { method: 'DELETE' }),
    onSuccess: (project) => {
      client.invalidateQueries({ queryKey: ['projects', platform] })
      pushToast(
        'success',
        `${project.name} deleted — its overrides stop being served immediately;` +
          ' restore it anytime from Settings › Client projects.',
      )
    },
    onError: (error) => pushToast('error', `Could not delete project: ${(error as Error).message}`),
  })
}

export function useRestoreProject(platform: string) {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (projectId: string) => api<Project>(`/api/projects/${projectId}/restore`, { method: 'POST' }),
    onSuccess: (project) => {
      client.invalidateQueries({ queryKey: ['projects', platform] })
      pushToast('success', `${project.name} restored.`)
    },
    onError: (error) => pushToast('error', `Could not restore project: ${(error as Error).message}`),
  })
}

/** Soft-deleted client projects for one platform — the only place a deleted
 * project can be found and restored again (see routers/editor.py's
 * `?deleted=true`). */
export function useDeletedProjects(platform: string | undefined) {
  return useQuery({
    queryKey: ['projects', platform, 'deleted'],
    enabled: Boolean(platform),
    queryFn: () => api<Project[]>(`/api/platforms/${platform}/projects${query({ deleted: true })}`),
  })
}

// --------------------------------------------------------------------------
// Activity feed (dashboard)
// --------------------------------------------------------------------------

export interface ActivityFilters {
  platform?: string
  locale?: string
  actorType?: string
  limit?: number
  offset?: number
}

export function useActivity(filters: ActivityFilters = {}) {
  const { platform, locale, actorType, limit = 20, offset = 0 } = filters
  return useQuery({
    queryKey: ['activity', platform ?? null, locale ?? null, actorType ?? null, limit, offset],
    queryFn: () =>
      api<ActivityPage>(
        `/api/activity${query({ platform, locale, actor_type: actorType, limit, offset })}`,
      ),
  })
}

// --------------------------------------------------------------------------
// AI actions + bulk review
// --------------------------------------------------------------------------

export function useAiTranslate(platform: string, projectId: string | undefined) {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (vars: { locales: string[]; keyIds?: string[]; onlyUntranslated?: boolean }) =>
      aiTranslate(projectId ?? '', {
        locales: vars.locales,
        key_ids: vars.keyIds,
        only_untranslated: vars.onlyUntranslated ?? true,
      }),
    onSuccess: (data) => {
      pushToast(
        'success',
        `${data.enqueued} AI job${data.enqueued === 1 ? '' : 's'} queued` +
          (data.skipped_manual ? `, ${data.skipped_manual} manual cell(s) skipped` : ''),
      )
      client.invalidateQueries({ queryKey: ['jobs', 'all'] })
      client.invalidateQueries({ queryKey: gridKeyFor(platform, projectId) })
    },
    onError: (error) => pushToast('error', `AI translate failed: ${(error as Error).message}`),
  })
}

export function useRetranslateCell(projectId: string | undefined) {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (vars: { keyId: string; locale: string }) =>
      retranslate({
        project_id: projectId ?? '',
        key_id: vars.keyId,
        locale: vars.locale,
      }),
    onSuccess: (_data, vars) => {
      pushToast('success', `Re-translation queued for ${vars.locale}`)
      client.invalidateQueries({ queryKey: ['jobs', 'all'] })
    },
    onError: (error) => pushToast('error', `Re-translate failed: ${(error as Error).message}`),
  })
}

// --------------------------------------------------------------------------
// Admin: users (P8)
// --------------------------------------------------------------------------

export function useUsers() {
  return useQuery({ queryKey: ['admin', 'users'], queryFn: () => api<AdminUser[]>('/api/admin/users') })
}

export function useCreateUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; password: string; role: Role; name?: string }) =>
      api<AdminUser>('/api/admin/users', { method: 'POST', body }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function usePatchUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      userId,
      ...body
    }: { userId: string; role?: Role; is_active?: boolean; password?: string; name?: string }) =>
      api<AdminUser>(`/api/admin/users/${userId}`, { method: 'PATCH', body }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useDeactivateUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => api<AdminUser>(`/api/admin/users/${userId}`, { method: 'DELETE' }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

/** Irreversible GDPR-style erasure -- distinct endpoint from deactivate above. */
export function useEraseUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, confirmEmail }: { userId: string; confirmEmail: string }) =>
      api<EraseUserResult>(`/api/admin/users/${userId}/erase`, {
        method: 'POST',
        body: { confirm_email: confirmEmail },
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

// --------------------------------------------------------------------------
// Admin: API keys (P8)
// --------------------------------------------------------------------------

export function useApiKeys() {
  return useQuery({
    queryKey: ['admin', 'api-keys'],
    queryFn: () => api<ApiKeyRecord[]>('/api/admin/api-keys'),
  })
}

export function useCreateApiKey() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { name: string; scope: ApiKeyScope; platform?: string | null }) =>
      api<ApiKeyCreated>('/api/admin/api-keys', { method: 'POST', body }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin', 'api-keys'] }),
  })
}

export function useRevokeApiKey() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (keyId: string) =>
      api<ApiKeyRecord>(`/api/admin/api-keys/${keyId}`, { method: 'DELETE' }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin', 'api-keys'] }),
  })
}

// --------------------------------------------------------------------------
// Admin: AI providers (BYOK) (P8)
// --------------------------------------------------------------------------

export function useAiProviders() {
  return useQuery({ queryKey: ['ai-providers'], queryFn: () => api<AiProvider[]>('/api/providers') })
}

export function useCreateAiProvider() {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (body: {
      provider: AiProviderName
      api_key: string
      model?: string
      config?: Record<string, unknown>
      enabled?: boolean
    }) => api<AiProvider>('/api/providers', { method: 'POST', body }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['ai-providers'] })
      pushToast('success', 'Provider added.')
    },
    onError: (error) => pushToast('error', `Could not add provider: ${(error as Error).message}`),
  })
}

export function usePatchAiProvider() {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: ({
      providerId,
      ...body
    }: {
      providerId: string
      api_key?: string
      model?: string
      config?: Record<string, unknown>
      enabled?: boolean
    }) => api<AiProvider>(`/api/providers/${providerId}`, { method: 'PATCH', body }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['ai-providers'] }),
    onError: (error) => pushToast('error', `Could not update provider: ${(error as Error).message}`),
  })
}

export function useDeleteAiProvider() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (providerId: string) =>
      api<{ deleted: string }>(`/api/providers/${providerId}`, { method: 'DELETE' }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['ai-providers'] }),
  })
}

export function useTestAiProvider() {
  return useMutation({
    mutationFn: (providerId: string) =>
      api<ProviderTestResult>(`/api/providers/${providerId}/test`, { method: 'POST' }),
  })
}

// --------------------------------------------------------------------------
// Locales: enable/disable, backfills on enable (P8)
// --------------------------------------------------------------------------

export function usePatchLocale() {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: ({ code, enabled }: { code: string; enabled: boolean }) =>
      api<LocalePatchResult>(`/api/locales/${code}`, { method: 'PATCH', body: { enabled } }),
    onSuccess: (result) => {
      client.invalidateQueries({ queryKey: ['locales'] })
      pushToast(
        'success',
        result.enabled
          ? `${result.code} enabled${result.backfilled ? ` — ${result.backfilled} translation job(s) queued` : ''}.`
          : `${result.code} disabled.`,
      )
    },
    onError: (error) => pushToast('error', `Could not update locale: ${(error as Error).message}`),
  })
}

/** Correct a locale's required CLDR plural forms (cldr_plurals.py seeds
 *  these from the language subtag, but an unverified language falls back to
 *  one/other, and a table entry could simply be wrong). Setting this always
 *  marks the locale plural_forms_verified=true. */
export function usePatchLocalePluralForms() {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: ({ code, pluralForms }: { code: string; pluralForms: string[] }) =>
      api<LocalePatchResult>(`/api/locales/${code}`, {
        method: 'PATCH',
        body: { plural_forms: pluralForms },
      }),
    onSuccess: (result) => {
      client.invalidateQueries({ queryKey: ['locales'] })
      pushToast('success', `${result.code} plural forms set to ${result.plural_forms.join(', ')}.`)
    },
    onError: (error) =>
      pushToast('error', `Could not update plural forms: ${(error as Error).message}`),
  })
}

export function useCreateLocale() {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (body: { code: string; name: string; formality: 'formal' | 'informal' | null }) =>
      api<LocaleInfo>('/api/locales', { method: 'POST', body }),
    onSuccess: (locale) => {
      client.invalidateQueries({ queryKey: ['locales'] })
      pushToast('success', `${locale.code} added — not yet assigned to any platform.`)
    },
    onError: (error) => pushToast('error', `Could not add locale: ${(error as Error).message}`),
  })
}

/** Untranslated-key count for one (platform, locale), for the preview shown
 * before the user commits to an AI-vs-manual choice. Works even before the
 * locale is assigned to the platform at all. */
export function useUntranslatedCount(platform: string | undefined, code: string | undefined) {
  return useQuery({
    queryKey: ['locales', 'untranslated-count', platform, code],
    enabled: Boolean(platform && code),
    queryFn: () =>
      api<UntranslatedCountResult>(
        `/api/platforms/${platform}/locales/${code}/untranslated-count`,
      ),
  })
}

export function useAssignLocale() {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (vars: { platform: string; code: string; translate: TranslateChoice }) =>
      api<LocaleAssignResult>(`/api/platforms/${vars.platform}/locales/${vars.code}`, {
        method: 'POST',
        body: { translate: vars.translate },
      }),
    onSuccess: (result) => {
      client.invalidateQueries({ queryKey: ['locales'] })
      client.invalidateQueries({ queryKey: ['platforms'] })
      client.invalidateQueries({ queryKey: ['jobs'] })
      pushToast(
        'success',
        result.translate === 'ai'
          ? `${result.locale} enabled for ${result.platform} — ${result.enqueued} AI job(s) queued.`
          : `${result.locale} enabled for ${result.platform} — translate it manually whenever you're ready.`,
      )
    },
    onError: (error) => pushToast('error', `Could not assign locale: ${(error as Error).message}`),
  })
}

export function useUnassignLocale() {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (vars: { platform: string; code: string }) =>
      api<LocaleUnassignResult>(`/api/platforms/${vars.platform}/locales/${vars.code}`, {
        method: 'DELETE',
      }),
    onSuccess: (result) => {
      client.invalidateQueries({ queryKey: ['locales'] })
      client.invalidateQueries({ queryKey: ['platforms'] })
      client.invalidateQueries({ queryKey: ['jobs'] })
      pushToast(
        'success',
        `${result.locale} removed from ${result.platform}` +
          (result.cancelled_jobs ? ` — ${result.cancelled_jobs} pending AI job(s) cancelled.` : '.'),
      )
    },
    onError: (error) => pushToast('error', `Could not unassign locale: ${(error as Error).message}`),
  })
}

// --------------------------------------------------------------------------
// Platforms (admin CRUD): create/rename/reformat/soft-delete/restore.
// --------------------------------------------------------------------------

export function useCreatePlatform() {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (body: { slug: string; name: string; default_format: string }) =>
      api<PlatformRecord>('/api/platforms', { method: 'POST', body }),
    onSuccess: (platform) => {
      client.invalidateQueries({ queryKey: ['platforms'] })
      pushToast(
        'success',
        `${platform.slug} created — it has zero keys until a dev runs POST /api/v1/push.`,
      )
    },
    onError: (error) => pushToast('error', `Could not create platform: ${(error as Error).message}`),
  })
}

export function usePatchPlatform() {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: ({
      slug,
      ...body
    }: {
      slug: string
      name?: string
      default_format?: string
    }) => api<PlatformRecord>(`/api/platforms/${slug}`, { method: 'PATCH', body }),
    onSuccess: (platform) => {
      client.invalidateQueries({ queryKey: ['platforms'] })
      pushToast('success', `${platform.slug} updated.`)
    },
    onError: (error) => pushToast('error', `Could not update platform: ${(error as Error).message}`),
  })
}

export function useDeletePlatform() {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: ({ slug, confirmActiveKeys }: { slug: string; confirmActiveKeys: boolean }) =>
      api<PlatformRecord>(
        `/api/platforms/${slug}${query({ confirm_active_keys: confirmActiveKeys })}`,
        { method: 'DELETE' },
      ),
    onSuccess: (platform) => {
      client.invalidateQueries({ queryKey: ['platforms'] })
      pushToast(
        'success',
        `${platform.slug} deleted — its keys/translations are kept and it can be restored.`,
      )
    },
    onError: (error) => pushToast('error', `Could not delete platform: ${(error as Error).message}`),
  })
}

export function useRestorePlatform() {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (platformId: string) =>
      api<PlatformRecord>(`/api/platforms/${platformId}/restore`, { method: 'POST' }),
    onSuccess: (platform) => {
      client.invalidateQueries({ queryKey: ['platforms'] })
      pushToast('success', `${platform.slug} restored.`)
    },
    onError: (error) => pushToast('error', `Could not restore platform: ${(error as Error).message}`),
  })
}

export function useMarkReviewed(platform: string, projectId: string | undefined) {
  const client = useQueryClient()
  const pushToast = useUi((state) => state.pushToast)
  return useMutation({
    mutationFn: (translationIds: string[]) => markReviewed(translationIds),
    onSuccess: (data) => {
      pushToast('success', `${data.updated} cell(s) marked reviewed`)
      client.invalidateQueries({ queryKey: gridKeyFor(platform, projectId) })
    },
    onError: (error) => pushToast('error', `Mark reviewed failed: ${(error as Error).message}`),
  })
}
